import React from 'react';
import { Trash2, CheckCircle } from 'lucide-react';
import { theme } from '../../styles/theme';
import { layerState, type DimensionPhase, type LayerState } from './phases';

interface DeduplicationStatsPanelProps {
  // Fields are optional at runtime: a resumed/partway job (or one whose stats
  // blob predates a field) can omit any of these. The panel must not crash on a
  // missing number — see the `?? 0` guards below.
  stats?: {
    dimensions_extracted?: number;
    dimensions_created?: number;
    dimensions_updated?: number;
    duplicates_removed_pre_ai?: number;
    conflicts_resolved_post_ai?: number;
    duplicates_skipped_final?: number;
    api_calls_made?: number;
    cost_usd?: number;
    total_cost_usd?: number;
  };
  // Live phase id from processing_details.current_phase — drives the
  // active/done/pending state of each layer row below (undefined for a
  // completed job renders every layer as done).
  currentPhase?: string;
}

export const DeduplicationStatsPanel: React.FC<DeduplicationStatsPanelProps> = ({ stats, currentPhase }) => {
  if (!stats) return null;

  const totalDuplicatesPrevented =
    (stats.duplicates_removed_pre_ai ?? 0) +
    (stats.conflicts_resolved_post_ai ?? 0) +
    (stats.duplicates_skipped_final ?? 0);

  const stateClasses: Record<LayerState, string> = {
    active: 'ring-2 ring-border-strong',
    done: '',
    pending: 'opacity-60',
  };
  const rowState = (phase: DimensionPhase): LayerState => layerState(phase, currentPhase);

  return (
    <div className="bg-bg-surface border border-border rounded-xl p-4">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Trash2 className="w-5 h-5 text-text-secondary" />
        <h3 className={`text-sm font-semibold ${theme.text.primary}`}>
          Duplicate summary
        </h3>
      </div>

      {/* 3-Layer Badge */}
      <div className="mb-4 p-3 bg-bg-subtle border border-qc-success/30 rounded-lg">
        <div className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-qc-success" />
          <span className="text-sm font-semibold text-qc-success">
             Duplicate prevention
          </span>
        </div>
      </div>

      {/* Layer 1: pre_dedup */}
      <div
        data-testid="dedup-layer-pre_dedup"
        data-state={rowState('pre_dedup')}
        className={`mb-3 p-3 bg-bg-subtle border border-border rounded-lg ${stateClasses[rowState('pre_dedup')]}`}
      >
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-bg-subtle flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-text-secondary">1</span>
          </div>
          <div className="flex-1">
            <h4 className={`text-sm font-semibold ${theme.text.primary} mb-1`}>
              Cleaned existing data
            </h4>
            <div className="flex items-center justify-between">
              <span className={`text-xs ${theme.text.tertiary}`}>
                Duplicates removed:
              </span>
              <span className={`text-lg font-bold ${theme.text.primary}`}>
                {stats.duplicates_removed_pre_ai}
              </span>
            </div>
            <p className={`text-xs ${theme.text.tertiary} mt-1`}>
              Kept the more specific measurement when the same value appeared twice.
            </p>
          </div>
        </div>
      </div>

      {/* Layer 2: post_dedup */}
      <div
        data-testid="dedup-layer-post_dedup"
        data-state={rowState('post_dedup')}
        className={`mb-3 p-3 bg-qc-warning/10 border border-qc-warning/30 rounded-lg ${stateClasses[rowState('post_dedup')]}`}
      >
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-qc-warning/10 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-qc-warning">2</span>
          </div>
          <div className="flex-1">
            <h4 className={`text-sm font-semibold ${theme.text.primary} mb-1`}>
              Resolved conflicts
            </h4>
            <div className="flex items-center justify-between">
              <span className={`text-xs ${theme.text.tertiary}`}>
                Conflicts resolved:
              </span>
              <span className={`text-lg font-bold ${theme.text.primary}`}>
                {stats.conflicts_resolved_post_ai}
              </span>
            </div>
            <p className={`text-xs ${theme.text.tertiary} mt-1`}>
              Chose the best match when a measurement was ambiguous.
            </p>
          </div>
        </div>
      </div>

      {/* Layer 3: final_dedup */}
      <div
        data-testid="dedup-layer-final_dedup"
        data-state={rowState('final_dedup')}
        className={`mb-4 p-3 bg-interactive-primary/10 border border-interactive-primary/30 rounded-lg ${stateClasses[rowState('final_dedup')]}`}
      >
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-interactive-primary/10 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-interactive-primary">3</span>
          </div>
          <div className="flex-1">
            <h4 className={`text-sm font-semibold ${theme.text.primary} mb-1`}>
              Skipped items already in the catalogue
            </h4>
            <div className="flex items-center justify-between">
              <span className={`text-xs ${theme.text.tertiary}`}>
                Duplicates skipped:
              </span>
              <span className={`text-lg font-bold ${theme.text.primary}`}>
                {stats.duplicates_skipped_final}
              </span>
            </div>
            <p className={`text-xs ${theme.text.tertiary} mt-1`}>
              Avoided creating duplicates that already exist.
            </p>
          </div>
        </div>
      </div>
      
      {/* Total Summary */}
      <div className="pt-3 border-t border-border">
        <div className="flex items-center justify-between">
          <span className={`text-sm font-semibold ${theme.text.primary}`}>
            Total Duplicates Prevented:
          </span>
          <span className="text-2xl font-bold text-text-primary">
            {totalDuplicatesPrevented}
          </span>
        </div>
      </div>
      
      {/* Additional Stats */}
      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <div className="p-2 bg-bg-base rounded">
          <span className={theme.text.tertiary}>Extracted:</span>
          <span className={`ml-2 font-semibold ${theme.text.primary}`}>
            {stats.dimensions_extracted}
          </span>
        </div>
        <div className="p-2 bg-bg-base rounded">
          <span className={theme.text.tertiary}>Created:</span>
          <span className={`ml-2 font-semibold ${theme.text.primary}`}>
            {stats.dimensions_created}
          </span>
        </div>
        <div className="p-2 bg-bg-base rounded">
          <span className={theme.text.tertiary}>Automated checks:</span>
          <span className={`ml-2 font-semibold ${theme.text.primary}`}>
            {stats.api_calls_made}
          </span>
        </div>
        <div className="p-2 bg-bg-base rounded">
          <span className={theme.text.tertiary}>Cost:</span>
          <span className={`ml-2 font-semibold ${theme.text.primary}`}>
            ${(stats.total_cost_usd ?? 0).toFixed(4)}
          </span>
        </div>
      </div>
    </div>
  );
};
